import { type RefObject, useCallback, useEffect, useRef } from 'react';
import { type HostInstance, type LayoutChangeEvent, type ScrollViewInstance } from 'react-native';
import {
  type AnimatedRef,
  useAnimatedReaction,
  useAnimatedRef,
  useScrollOffset,
} from 'react-native-reanimated';
import type Animated from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';

import { useActorRef, useSelector } from '@xstate/react';

import { scrollspyMachine } from './machine';

// ─────────────────────────────────────────────────────────────────────────────

export const useScrollspy = (params?: UseScrollspyMachineParams) => {
  const { defaultTargetId, onActiveTargetIdChange } = params ?? {};

  // ─── Refs ──────────────────────────────────────────────────────────────

  const scrollViewRef = useAnimatedRef<Animated.ScrollView>();
  const scrollOffset = useScrollOffset(scrollViewRef);
  const navScrollViewRef = useRef<ScrollViewInstance>(null);

  // ─── Machine ───────────────────────────────────────────────────────────

  const actorRef = useActorRef(scrollspyMachine, {
    input: { defaultTargetId, scrollOffset, scrollViewRef, navScrollViewRef },
  });

  const activeTargetId = useSelector(actorRef, (snapshot) => snapshot.context.activeTargetId);

  const pendingDefaultTargetId = useSelector(
    actorRef,
    (snapshot) => snapshot.context.defaultTargetId,
  );

  const hasDefaultMeasurement = useSelector(actorRef, (snapshot) =>
    pendingDefaultTargetId ? Boolean(snapshot.context.measurements[pendingDefaultTargetId]) : false,
  );

  // ─── Helpers ───────────────────────────────────────────────────────────

  const scrollToDefaultTarget = useCallback(() => {
    if (!pendingDefaultTargetId || !hasDefaultMeasurement) return;

    actorRef.send({ type: 'defaultTarget.scrollTo', targetId: pendingDefaultTargetId });
  }, [pendingDefaultTargetId, hasDefaultMeasurement, actorRef]);

  // ─── Scroll Tracking ───────────────────────────────────────────────────

  const sendScroll = useCallback(() => {
    actorRef.send({ type: 'scroll' });
  }, [actorRef]);

  // ─────────────────────────────────────────────────────────────────────

  const lastNotifiedTargetIdRef = useRef(activeTargetId);

  // ─── Measurement ───────────────────────────────────────────────────────

  const measureTargetFactory = useCallback(
    (targetId: string, targetRef: RefObject<unknown>) => {
      return (_x: number, y: number, _width: number, height: number) => {
        actorRef.send({ type: 'target.measure', targetId, targetRef, measurement: { y, height } });
      };
    },
    [actorRef],
  );

  const measureCoordinates = useCallback(
    (targetId: string, targetRef: RefObject<unknown>) => {
      const measureTarget = measureTargetFactory(targetId, targetRef);

      requestAnimationFrame(() => {
        const targetNode = (targetRef.current as HostInstance | null) ?? null;
        const scrollViewNode = (scrollViewRef.current as HostInstance | null) ?? null;

        if (!scrollViewNode || !targetNode) return;

        targetNode?.measureLayout?.(scrollViewNode, measureTarget);
      });
    },
    [measureTargetFactory, scrollViewRef],
  );

  const deregisterTarget = useCallback(
    (targetId: string) => {
      actorRef.send({ type: 'target.deregister', targetId });
    },
    [actorRef],
  );

  // ─── Event Forwarders ──────────────────────────────────────────────────

  const scrollTo = useCallback(
    (targetId: string) => {
      actorRef.send({ type: 'target.scrollTo', targetId });
    },
    [actorRef],
  );

  const onScrollViewContentSizeChange = useCallback(
    (_width: number, height: number) => {
      actorRef.send({ type: 'scrollView.contentSizeChange', height });
    },
    [actorRef],
  );

  const onScrollViewLayout = useCallback(
    (event: LayoutChangeEvent) => {
      actorRef.send({ type: 'scrollView.layout', height: event.nativeEvent.layout.height });
    },
    [actorRef],
  );

  const notifyOnTargetChange = useCallback(() => {
    if (!activeTargetId || !onActiveTargetIdChange) return;
    if (activeTargetId === lastNotifiedTargetIdRef.current) return;

    lastNotifiedTargetIdRef.current = activeTargetId;

    onActiveTargetIdChange(activeTargetId);
  }, [activeTargetId, onActiveTargetIdChange]);

  // ─── Effects ───────────────────────────────────────────────────────────

  useEffect(scrollToDefaultTarget, [scrollToDefaultTarget]);
  useEffect(notifyOnTargetChange, [notifyOnTargetChange]);

  // NOTE: Callbacks must be inline — Reanimated babel plugin converts them to worklets.
  useAnimatedReaction(
    () => scrollOffset.value,
    () => {
      scheduleOnRN(sendScroll);
    },
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    activeTargetId,
    scrollViewRef,
    navScrollViewRef,
    scrollOffset,
    scrollTo,
    measureCoordinates,
    deregisterTarget,
    onScrollViewContentSizeChange,
    onScrollViewLayout,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseScrollspyMachineParams = {
  defaultTargetId?: string;
  onActiveTargetIdChange?: (targetId: string) => void;
};

export type ScrollspyScrollViewRef = AnimatedRef<Animated.ScrollView>;
